import { AxiosInstance } from "axios";
import { IConnection, IConnectionLog, IConnectionStats, IUserConnectionStatsResponse } from "../interfaces/IConnection";
import { IPagedFilterRequest } from "../interfaces/IPagedFilterResults";
import { IPagedFilterResponse } from "../interfaces/IPagedFilterResponse";
import { buildPagedFilterQuery } from "../utils/queryHelpers";
import { IMessage } from "../interfaces/IChatUser";

export class ConnectionService {
    private apiClient: AxiosInstance;

    constructor(apiClient: AxiosInstance) {
        this.apiClient = apiClient;
    }

    async getAll(request: IPagedFilterRequest): Promise<IPagedFilterResponse<IConnection>> {
        try {
            const query = buildPagedFilterQuery(request);
            const response = await this.apiClient.get(`/participant/connections?${query}`);
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data || "Failed to fetch connections");
        }
    }

    async get(id: string): Promise<IConnection> {
        try {
            const response = await this.apiClient.get(`/participant/connections/${id}`);
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data || "Failed to fetch connection");
        }
    }

    async create(otherUserId: string, eventId?: string): Promise<IConnection> {
        try {
            const response = await this.apiClient.post('/participant/connections', JSON.stringify(otherUserId), {
                headers: { 'Content-Type': 'application/json' },
                params: eventId ? { eventId } : undefined,
            });
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data || 'Failed to create connection');
        }
    }

    async accept(otherUserId: string): Promise<void> {
        try {
            await this.apiClient.put(`/participant/connections/${otherUserId}/accept`);
        } catch (error: any) {
            throw new Error(error.response?.data || "Failed to accept connection");
        }
    }

    async reject(otherUserId: string): Promise<void> {
        try {
            await this.apiClient.put(`/participant/connections/${otherUserId}/reject`);
        } catch (error: any) {
            throw new Error(error.response?.data || "Failed to reject connection");
        }
    }

    async remove(otherUserId: string): Promise<void> {
        try {
            await this.apiClient.delete(`/participant/connections/${otherUserId}`);
        } catch (error: any) {
            throw new Error(error.response?.data || "Failed to remove connection");
        }
    }

    async getLogs(eventId: string, page = 1, pageSize = 20): Promise<IPagedFilterResponse<IConnectionLog>> {
        try {
            const response = await this.apiClient.get(
                `/admin/event/${eventId}/connections/logs`,
                { params: { page, pageSize } }
            );
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data || "Failed to fetch connection logs");
        }
    }

    async getStats(eventId: string): Promise<IConnectionStats> {

        try {
            const response = await this.apiClient.get(`/admin/events/${eventId}/connections/stats`);
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data || "Failed to fetch connection stats");
        }
    }
    async getUserStats(eventId: string): Promise<IUserConnectionStatsResponse> {
        try {
            const response = await this.apiClient.get(`/admin/event/${eventId}/connections/users-stats`);
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data || "Failed to fetch connection stats");
        }
    }
    async getMessages(connectionId: string, page = 1, pageSize = 50): Promise<IMessage[]> {
        try {
            const response = await this.apiClient.get(`/connections/${connectionId}/messages`, {
                params: { page, pageSize },
            });
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data || "Failed to fetch messages");
        }
    }

    async sendMessage(connectionId: string, content: string): Promise<IMessage> {
        try {
            const response = await this.apiClient.post(`/connections/${connectionId}/messages`, { content });
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data || "Failed to send message");
        }
    }

}
